import type { ProductDetail, ProductUnit, VatType } from '@/api/models';
import type {
    ProductUpdateRequest,
    ProductVariantUpdateRequest,
} from '@/api/handlers/resources/products/requests';
import { buildVariantLabel } from '../productDetail/productDetailMeta';

/**
 * Types + pure helpers for the **fields-only** edit form. Seeding turns the
 * loaded `ProductDetail` into editable string drafts; `buildUpdateRequest`
 * parses them back into the `PUT /products/{id}` body. By construction this
 * never produces option-axis changes, variant add/remove, or batch edits — the
 * draft only carries metadata + each existing variant's editable fields (keyed
 * by the variant `id`).
 */

/** Editable fields of one existing variant (raw string inputs) + read-only id/label. */
export type EditVariantDraft = {
    id: string;
    label: string;
    salePrice: string;
    wholesalePrice: string;
    minimumQuantity: string;
    weight: string;
    volume: string;
    saleEndDate: string;
};

export type EditProductFormState = {
    name: string;
    sku: string;
    unit: ProductUnit;
    vatType: VatType;
    categoryId: string | null;
    brandId: string | null;
    variants: EditVariantDraft[];
};

const numberToInput = (value: number | null): string =>
    value === null ? '' : String(value);

/** Seed the editable draft from the loaded product graph. */
export const seedEditForm = (detail: ProductDetail): EditProductFormState => ({
    name: detail.name,
    sku: detail.sku ?? '',
    unit: detail.unit,
    vatType: detail.vatType,
    categoryId: detail.category?.id ?? null,
    brandId: detail.brand?.id ?? null,
    variants: detail.variants.map((variant) => ({
        id: variant.id,
        label: buildVariantLabel(variant),
        salePrice: numberToInput(variant.salePrice),
        wholesalePrice: numberToInput(variant.wholesalePrice),
        minimumQuantity: numberToInput(variant.minimumQuantity),
        weight: numberToInput(variant.weight),
        volume: numberToInput(variant.volume),
        // The detail endpoint doesn't return saleEndDate; leave it blank so an
        // untouched form omits it from the PUT.
        saleEndDate: '',
    })),
});

const parseOptionalNumber = (value: string): number | undefined => {
    const trimmed = value.trim();
    if (trimmed === '') {
        return undefined;
    }
    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? undefined : parsed;
};

const parseRequiredNumber = (value: string): number => {
    const parsed = Number(value.trim());
    return Number.isNaN(parsed) ? 0 : parsed;
};

const buildVariantUpdate = (
    variant: EditVariantDraft,
): ProductVariantUpdateRequest => {
    const request: ProductVariantUpdateRequest = {
        id: variant.id,
        salePrice: parseRequiredNumber(variant.salePrice),
        minimumQuantity: parseRequiredNumber(variant.minimumQuantity),
    };
    const wholesale = parseOptionalNumber(variant.wholesalePrice);
    if (wholesale !== undefined) {
        request.wholesalePrice = wholesale;
    }
    const weight = parseOptionalNumber(variant.weight);
    if (weight !== undefined) {
        request.weight = weight;
    }
    const volume = parseOptionalNumber(variant.volume);
    if (volume !== undefined) {
        request.volume = volume;
    }
    if (variant.saleEndDate) {
        request.saleEndDate = variant.saleEndDate;
    }
    return request;
};

/** Assemble the `PUT /products/{id}` body from the editable draft. */
export const buildUpdateRequest = (
    state: EditProductFormState,
): ProductUpdateRequest => {
    const request: ProductUpdateRequest = {
        name: state.name.trim(),
        unit: state.unit,
        vatType: state.vatType,
        categoryId: state.categoryId ?? '',
        variants: state.variants.map(buildVariantUpdate),
    };
    if (state.sku.trim()) {
        request.sku = state.sku.trim();
    }
    if (state.brandId) {
        request.brandId = state.brandId;
    }
    return request;
};

/** Light client-side validation. Returns the first problem's i18n key, or null. */
export const validateEditForm = (
    state: EditProductFormState,
): string | null => {
    if (!state.name.trim()) {
        return 'products.edit.errors.nameRequired';
    }
    if (!state.categoryId) {
        return 'products.edit.errors.categoryRequired';
    }
    for (const variant of state.variants) {
        if (parseOptionalNumber(variant.salePrice) === undefined) {
            return 'products.edit.errors.salePriceRequired';
        }
    }
    return null;
};
